import { CheckOutlined, EditOutlined, LoadingOutlined, UsergroupAddOutlined, WarningOutlined } from "@ant-design/icons";
import { Button, Col, Divider, Input, Modal, Row, Select, notification } from "antd";
import React, { useEffect, useState } from "react";
import { states, policy_type } from "../../assets/utils/config";
import lib, { isEqual } from "../../assets/utils/lib";
import { useAuth } from "../../assets/utils/useAuth";
import { usePermissions } from "../../assets/utils/usePermissions";
import { RenewPolicy } from "./policyRenew";
import { renderButton } from "./renderButton";
import { DependantEdit } from "./enroleeData";
import { renderField } from "./renderField";
import { same } from "../../assets/utils/Helpers";
import { formatPlanEnrollmentErrorMessage } from "../plans/planCatalogHelpers";

const { TextArea } = Input;
const { confirm } = Modal;

export const PolicyHolder = ({ data, showAllActions = false }) => {
  const [show, setShow] = useState(false);

  const launch = (e) => {
    setShow(!show);
  };

  return (
    <>
      <Row
        style={{
          padding: "16px",
          borderRadius: "8px",
          background: "#fff",
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
          margin: "2px 0",
          cursor: "pointer",
          transition: "box-shadow 0.3s ease",
        }}
        onClick={() => launch(data, "clients")}
      >
        <Col span={8}>
          <Row>
            <Col span={8}>Name: </Col>
            <Col span={16}>{data?.name}</Col>
          </Row>
        </Col>
        <Col span={10}>
          <Row>
            <Col span={10}>Insurance Package: </Col>
            <Col span={14}>{data?.insurance_package}</Col>
          </Row>
        </Col>
        <Col span={6}>
          <Row>
            <Col span={12}>Premium Type: </Col>
            <Col span={12}>{data?.premium_type?.toUpperCase()}</Col>
          </Row>
        </Col>
        <Col span={8}>
          <Row>
            <Col span={8}>Enrolee Size: </Col>
            <Col span={16}>{data?.enrolee_size}</Col>
          </Row>
        </Col>
        <Col span={10}>
          <Row>
            <Col span={10}>Email: </Col>
            <Col span={14}>{data?.email}</Col>
          </Row>
        </Col>
        <Col span={6}>
          <Row>
            <Col span={12}>Phone: </Col>
            <Col span={12}>{data?.phone}</Col>
          </Row>
        </Col>
        <Col span={8}>
          <Row>
            <Col span={8}>Policy Number: </Col>
            <Col span={16}>{data?.policy_no}</Col>
          </Row>
        </Col>
        <Col span={10}>
          <Row>
            <Col span={10}>Reg. Date: </Col>
            <Col span={14}>{data?.date}</Col>
          </Row>
        </Col>
        <Col span={6}>
          <Row>
            <Col span={12}>State / City: </Col>
            <Col span={12}>
              {data?.state?.toUpperCase()} / {data?.city?.toUpperCase()}
            </Col>
          </Row>
        </Col>
      </Row>

      <PolicyHolderModal
        data={data}
        display={show}
        action={() => setShow(false)}
        onclose={launch}
        showAllActions={showAllActions}
      />
    </>
  );
};

export const PolicyHolderModal = ({
  data,
  display,
  action,
  onclose = () => null,
  showAllActions = false,
  viewOnly = false,
  modalZIndex,
  modalClassName = "",
}) => {
  const { user } = useAuth();
  const { hasAnyPermissionWithLegacy } = usePermissions();
  // Two capabilities were collapsed into one legacy role id here.
  // 1) Editing the policy holder's personal details (legacy 1 / 2 / 5). Head of Finance (5)
  //    holds no clients.* permission, so its seeded payments permissions stand in for it.
  const canEditPolicyDetails = hasAnyPermissionWithLegacy(
    ['clients.update', 'clients.manage_all', 'admin.*', 'payments.create', 'corporations.manage_payments'],
    [1, 2, 5],
  );
  // 2) Premium / plan-category / renewal commercials (legacy 5 only).
  //    plans.manage is catalogued but seeded to no role today.
  const canManagePolicyCommercials = hasAnyPermissionWithLegacy(
    ['payments.create', 'corporations.manage_payments', 'plans.manage', 'admin.*'],
    [5],
  );
  const [n_data, setNdata] = useState(data);
  const [edit, setEdit] = useState(false);
  const [update, setUpdate] = useState(false);

  const [validatingSocial, setValidatingSocial] = useState(false);
  const [socialDetail, setSocialDetail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [planOptions, setPlanOptions] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);

  useEffect(() => {
    if (!data || !n_data) return;
    setUpdate(!same(data, n_data));
  }, [n_data, data]); //eslint-disable-line react-hooks/exhaustive-deps

  const [loadingStates, setLoadingStates] = useState({
    loading: false,
    update: false,
    activate: false,
    disable: false,
    flag: false,
    revoke: false,
    validateSocial: false,
    category: false,
    edit: false
  });

  const setLoadingFlags = (key, value) => {
    setLoadingStates(d => ({ ...d, [key]: value }))
  }

  const isLoading = (key) => Object.values(loadingStates).some(Boolean);

  const expiryUpdateHandler = () => {
  }

  useEffect(() => {
    if (display && n_data?.category === 'corporate') {
      verifyCode();
    }
  }, [display]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!display) return;
    let mounted = true;
    const loadPlans = async () => {
      setLoadingPlans(true);
      try {
        const socialNo = String(n_data?.social_no || data?.social_no || '').trim();
        const isCorporate = String(n_data?.category || data?.category || '').toLowerCase() === 'corporate';

        // Corporate clients: only plans assigned to the org via Manage plans.
        if (isCorporate && socialNo) {
          const catalog = lib.parseOrganizationPlanCatalog(
            await lib.getOrganizationPlanCatalog(socialNo),
          );
          if (!catalog.ok) {
            if (mounted) setPlanOptions([]);
            return;
          }
          const options = catalog.rows
            .filter((plan) => Number.isFinite(Number(plan?.plan_id)))
            .map((plan) => ({
              label: plan.plan_name || `Plan ${plan.plan_id}`,
              value: Number(plan.plan_id),
              plan_name: plan.plan_name || '',
            }));
          if (mounted) setPlanOptions(options);
          return;
        }

        // Self-pay / no org: retail catalog (exclude utility/archived when flags exist).
        const resp = await lib.getAdminPlansCatalog({ scope: 'public' });
        const list = lib.parseAdminPlansList(resp);
        if (!list.ok) {
          if (mounted) setPlanOptions([]);
          return;
        }
        const options = list.plans
          .filter((plan) => Number.isFinite(Number(plan?.plan_id)))
          .filter((plan) => {
            if (plan.plan_archived === 1 || plan.plan_archived === true || plan.plan_archived === '1') return false;
            if (plan.plan_purchasable === 0 || plan.plan_purchasable === false || plan.plan_purchasable === '0') return false;
            return true;
          })
          .map((plan) => {
            const tierLabel = plan?.tier_name || (plan?.tier_id != null ? `Tier ${plan?.tier_id}` : 'No tier');
            return {
              label: `${plan?.plan_name || `Plan ${plan?.plan_id}`} (${tierLabel})`,
              value: Number(plan?.plan_id),
              plan_name: plan?.plan_name || '',
            };
          });
        if (mounted) setPlanOptions(options);
      } finally {
        if (mounted) setLoadingPlans(false);
      }
    };
    loadPlans();
    return () => {
      mounted = false;
    };
  }, [display, n_data?.category, n_data?.social_no, data?.category, data?.social_no]);

  useEffect(() => {
    if (!planOptions.length) return;
    const currentPlanId = Number(n_data?.plans_id);
    if (Number.isFinite(currentPlanId) && currentPlanId > 0) return;
    const matchedPlan = planOptions.find(
      (plan) => String(plan?.plan_name || '').toLowerCase() === String(n_data?.insurance_package || '').toLowerCase()
    );
    if (matchedPlan) {
      setNdata((d) => ({ ...d, plans_id: matchedPlan.value, insurance_package: matchedPlan.plan_name }));
    }
  }, [planOptions, n_data?.plans_id, n_data?.insurance_package]);

  useEffect(() => {
    const vn = Object.entries(n_data).filter((v, i) => v[1] !== data[v[0]]);
    if (vn.length) setUpdate(true);
  }, [n_data]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateData = async (e, data) => {
    const actionType = Number(e);
    const loadingMap = {
      1: 'update',
      2: 'activate',
      3: 'disable',
      4: 'flag',
      5: 'revoke',
    };

    const key = loadingMap[actionType];
    setLoadingFlags(key, true);
    setEdit(false);

    lib
      .updateUserData({ ...n_data, ...data })
      .then((res) => res.data)
      .then((data) => {
        if (!data?.error) {
          notification.success({
            message: data?.message,
            placement: "topLeft",
          });
        } else {
          notification.error({
            message: formatPlanEnrollmentErrorMessage(data, {
              fallback: data?.message || 'Update failed',
            }),
            placement: "topLeft",
          });
        }
      })
      .catch((err) => {
        setEdit(true);
        notification.error({
          message: formatPlanEnrollmentErrorMessage(err, {
            fallback: err?.message || 'Update failed',
          }),
          placement: 'topLeft',
        });
      })
      .finally(() => {
        setLoadingFlags(key, false);
        setLoadingFlags('update', false);
        setUpdate(false);
        action();
      });
  };

  const handleFocusOut = async (e) => { // eslint-disable-line
    setValidatingSocial(true);
    let obj = {
      social_no: n_data?.social_no,
      mode: "corporate",
    };

    let reqData = await lib.validatSocial(obj);
    if (reqData.status === "error") {
      notification.error({ message: reqData.msg, placement: "topLeft" });
    }

    if (reqData?.error === 0) {
      setSocialDetail(reqData?.data);
    }
    setValidatingSocial(false);
  };

  const configFlag = {
    title: (
      <>Flag - {data?.policy_no}</>
    ),
    onOk: () => {
      updateData(4);
    },
    okText: "Raise Flag",
    width: 700,
    centered: true,
    maskClosable: true,
    content: (
      <>
        <Row gutter={[16, 16]}>
          <Col span={24}>Flag Memo: </Col>
          <Col span={24}>
            <TextArea
              onChange={(e) =>
                setNdata((d) => ({ ...d, flag_reason: e.target.value }))
              }
              style={{ width: "100%" }}
              rows={4}
            />
          </Col>
        </Row>
      </>
    ),
  };

  const configUnFlag = {
    title: (
      <>Unflag - {data?.policy_no}</>
    ),
    onOk() {
      updateData(4);
    },
    centered: true,
    maskClosable: true,
    okText: "Remove Flag",
    content: (
      <>
        <Row>
          <Col span={24}>Flag Memo: </Col>
          <Col span={24}>
            <TextArea
              onChange={(e) => setNdata((d) => ({ ...d, flag_reason: e }))}
              style={{ width: "100%" }}
              rows={4}
            />
          </Col>
        </Row>
      </>
    ),
  };

  const focalData = { category: data?.category, social_no: data?.social_no };
  const [updateCategory, setUpdateCategory] = useState(false);
  const [updateCategoryValidating, setUpdateCategoryValidating] = useState(false);
  const [categoryUpdateData, setCategoryUpdateData] = useState({ ...focalData });
  const [categoryVerified, setCategoryVerified] = useState(false);
  const [UpdateSocialDetail, setUpdateSocialDetail] = useState("");
  const [showEnrolleeAddition, setShowEnrolleeAddition] = useState(false);
  const [changeEmailVisible, setChangeEmailVisible] = useState(false);
  const [newEmail, setNewEmail] = useState(data?.email || '');
  const [updatingEmail, setUpdatingEmail] = useState(false);
  const [changePhoneVisible, setChangePhoneVisible] = useState(false);
  const [newPhone, setNewPhone] = useState(data?.phone || '');
  const [updatingPhone, setUpdatingPhone] = useState(false);

  // Keep modal state in sync when parent passes a new policy object
  useEffect(() => {
    setNdata(data || {});
    setNewEmail(data?.email || '');
    setNewPhone(data?.phone || '');
    setCategoryUpdateData({
      category: data?.category,
      social_no: data?.social_no,
    });
  }, [data]);

  // Normalize for case-insensitive comparison of social_no in Plan Category Edit modal
  const normalizedCategoryUpdateData = {
    ...categoryUpdateData,
    social_no: (categoryUpdateData?.social_no || '').toLowerCase(),
    category: (categoryUpdateData?.category || '').toLowerCase(),
  };
  const normalizedFocalData = {
    ...focalData,
    social_no: (focalData?.social_no || '').toLowerCase(),
    category: (focalData?.category || '').toLowerCase(),
  };

  // Determine if verification is required and whether update can proceed
  const isCorporateCategory = (categoryUpdateData?.category || '').toLowerCase() === 'corporate';
  const isSelfPayCategory = (categoryUpdateData?.category || '').toLowerCase() === 'self pay';
  const normalizedCategoryValue = (categoryUpdateData?.category || '').toLowerCase();
  const hasSocialNo = !!(categoryUpdateData?.social_no && categoryUpdateData.social_no.trim().length);
  const canSubmitCategoryUpdate =
    !loading &&
    (
      isSelfPayCategory ||
      (isCorporateCategory && categoryVerified && hasSocialNo)
    ) &&
    !isEqual(normalizedCategoryUpdateData, normalizedFocalData);

  const updateUseCategory = () => {
    if (
      categoryUpdateData?.category === "self pay" &&
      categoryUpdateData?.social_no
    )
      categoryUpdateData.social_no = "";

    setLoading(true)

    lib
      .updateUserData({ ...n_data, ...categoryUpdateData })
      .then((res) => res.data)
      .then((data) => {
        if (!data?.error) {
          notification.success({
            message: data?.message,
            placement: "topLeft",
          });
          setUpdateCategory()
        } else {
          notification.error({
            message: data?.message,
            placement: "topLeft",
          });
        }
      })
      .catch((err) => { })
      .finally(() => {
        setLoading(false)
      });
  };

  const verifyCode = async (e) => {
    setUpdateCategoryValidating(true);
    setCategoryVerified(false);
    let obj = {
      social_no: categoryUpdateData?.social_no || data?.social_no,
      mode: categoryUpdateData?.category || data?.category,
    };

    let reqData = await lib.validatSocial(obj);
    if (Number(reqData?.data?.error) === 0) {
      setUpdateSocialDetail(reqData?.data?.data);
      setSocialDetail(reqData?.data?.data)
      setCategoryVerified(true);
    } else {
      notification.error({
        message: reqData?.data?.message,
        placement: "topLeft",
      });
    }
    setValidatingSocial(false)
    setUpdateCategoryValidating(false);
  };

  const hasEditableRecord = Number(data?.status) || Number(data?.updated) || Number(data?.is_new);
  const canEditPersonalDetails = !!user && canEditPolicyDetails && hasEditableRecord;
  const canEditPremiumDetails = !!user && canManagePolicyCommercials && hasEditableRecord;
  const canEditAnySection = canEditPersonalDetails || canEditPremiumDetails;
  const canEdit = () => canEditPersonalDetails;

  const canUpdateClientEmail = (() => {
    const permissions = user?.profile?.permissions || [];
    const hasExplicitPermission = Array.isArray(permissions) && (
      permissions.includes('clients.update_email') || permissions.includes('admin.*')
    );
    // Keep permission-based control, but also expose email update to users
    // allowed to edit personal details (CS/CFO/admin in this modal context).
    return hasExplicitPermission || canEditPersonalDetails;
  })();

  // Same trust level as the login email: the phone is the client's login number.
  const canUpdateClientPhone = (() => {
    const permissions = user?.profile?.permissions || [];
    const hasExplicitPermission = Array.isArray(permissions) && (
      permissions.includes('clients.update_phone') || permissions.includes('clients.update_email') || permissions.includes('admin.*')
    );
    return hasExplicitPermission || canEditPersonalDetails;
  })();

  const handleChangePhone = async () => {
    const phone = String(newPhone || '').trim();
    const policyNo = n_data?.policy_no || data?.policy_no;

    if (!policyNo) {
      notification.error({ message: 'Policy number is required', placement: 'topLeft' });
      return;
    }
    const digits = phone.replace(/\D+/g, '');
    if (!phone || digits.length < 7 || digits.length > 15) {
      notification.error({ message: 'Please enter a valid phone number (7 to 15 digits)', placement: 'topLeft' });
      return;
    }

    setUpdatingPhone(true);
    try {
      const resp = await lib.updateClientPhone(policyNo, phone);
      const body = resp?.data || resp;

      if (Number(body?.error) === 0) {
        const updatedPhone = body?.data?.phone || phone;
        setNdata((prev) => ({ ...prev, phone: updatedPhone }));
        notification.success({
          message: body?.message || 'Client phone updated successfully',
          placement: 'topLeft',
        });
        setChangePhoneVisible(false);
        return;
      }

      notification.error({
        message: body?.message || 'Failed to update client phone',
        placement: 'topLeft',
      });
    } catch (err) {
      const statusCode = err?.response?.status;
      const backendMessage = err?.response?.data?.message;
      let messageText = backendMessage || 'Failed to update client phone';

      if (statusCode === 409) messageText = backendMessage || 'Phone number already in use';
      if (statusCode === 403 && !backendMessage) messageText = 'You do not have permission to update client phone';
      if (statusCode === 404 && !backendMessage) messageText = 'Client not found for this policy number';
      if (statusCode === 400 && !backendMessage) messageText = 'Invalid phone number or policy number';

      notification.error({
        message: messageText,
        placement: 'topLeft',
      });
    } finally {
      setUpdatingPhone(false);
    }
  };

  const handleChangeEmail = async () => {
    const email = String(newEmail || '').trim();
    const policyNo = n_data?.policy_no || data?.policy_no;

    if (!policyNo) {
      notification.error({ message: 'Policy number is required', placement: 'topLeft' });
      return;
    }

    if (!email) {
      notification.error({ message: 'Please enter an email address', placement: 'topLeft' });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      notification.error({ message: 'Please enter a valid email address', placement: 'topLeft' });
      return;
    }

    setUpdatingEmail(true);
    try {
      const resp = await lib.updateClientEmail(policyNo, email);
      const body = resp?.data || resp;

      if (Number(body?.error) === 0) {
        const updatedEmail = body?.data?.email || email;
        setNdata((prev) => ({ ...prev, email: updatedEmail }));
        notification.success({
          message: body?.message || 'Client email updated successfully',
          placement: 'topLeft',
        });
        setChangeEmailVisible(false);
        return;
      }

      notification.error({
        message: body?.message || 'Failed to update client email',
        placement: 'topLeft',
      });
    } catch (err) {
      const statusCode = err?.response?.status;
      const backendMessage = err?.response?.data?.message;
      let messageText = backendMessage || 'Failed to update client email';

      if (statusCode === 409) messageText = backendMessage || 'Email already in use';
      if (statusCode === 403 && !backendMessage) messageText = 'You do not have permission to update client email';
      if (statusCode === 404 && !backendMessage) messageText = 'Client not found for this policy number';
      if (statusCode === 400 && !backendMessage) messageText = 'Invalid email or policy number';

      notification.error({
        message: messageText,
        placement: 'topLeft',
      });
    } finally {
      setUpdatingEmail(false);
    }
  };

  return (
    <>
      <Modal
        rootClassName={modalClassName || undefined}
        className={modalClassName ? `${modalClassName}__dialog` : undefined}
        title={
          <Row gutter={5} justify={'start'} align={'middle'} className={modalClassName ? `${modalClassName}__titlebar` : undefined}>
            <Col style={{ fontWeight: 'bolder' }}>Client - {data?.policy_no}</Col>
            <Divider type="vertical" style={{ borderColor: 'black' }} />
            <Col>
              {renderButton({ type: 'link', label: <span style={{ fontWeight: 'bolder', cursor: 'default' }}>{Number(data?.status) ? "Active" : "Inactive"}</span>, danger: !Number(data?.status) })}
            </Col>
            {canManagePolicyCommercials && data?.category?.toLowerCase() !== 'corporate' && Number(data?.status) ? <>
              <Divider type="vertical" style={{ borderColor: 'black' }} />
              <Col>
                <RenewPolicy data={data} action={expiryUpdateHandler} key={data?.policy_no} />
              </Col>
            </> : null}
            {
              canEditAnySection ? <>
                <Divider type="vertical" style={{ borderColor: 'black' }} />
                <Col style={{ textAlign: "right", alignContent: "flex-end" }}>
                  {renderButton({
                    label: edit ? <>Close Edit</> : <>Edit</>,
                    isDisabled: isLoading(),
                    onClick: () => setEdit(!edit),
                    icon: edit ? <CheckOutlined /> : <EditOutlined />
                  })
                  }
                </Col>
              </> : null
            }
            {update && canEditAnySection ? <>
              <Divider type="vertical" />
              <Col >
                {
                  renderButton({
                    label: loadingStates?.update ? <span><LoadingOutlined /> Updating</span> : <>Update</>,
                    key: 'update',
                    isDisabled: isLoading(),
                    loadingState: loadingStates?.update,
                    actionType: 3,
                    onClick: () => updateData(1),
                  })
                }
              </Col>
            </> : null}
            {Number(data?.status) ? (<>
              <Divider type="vertical" style={{ borderColor: 'black' }} />
              {renderButton({
                icon: <UsergroupAddOutlined />,
                onClick: () => setShowEnrolleeAddition(true),
                label: 'Add An Enrollee',
                isDisabled: isLoading(),
              })}</>
            ) : null}
          </Row>
        }
        centered
        open={display}
        onCancel={onclose}
        zIndex={modalZIndex}
        footer={
          <>
            {Number(data?.status) && canEdit() ? (
              <Button
                type="primary"
                disabled={isLoading()}
                style={{ backgroundColor: "grey", border: "1px solid red" }}
                onClick={() => {
                  updateData(3, { status: 0 });
                }}
              >
                {loadingStates?.disable ? <LoadingOutlined /> : <>Disable</>}
              </Button>
            ) : (
              canEdit() && !Number(data?.status) ? <>
                <Button
                  type="primary"
                  disabled={loadingStates?.activate}
                  onClick={() =>
                    updateData(2, { status: 1, update_required: 0, paymentStatus: 1, updated: Number(data?.updated) || 1, is_new: 0 })
                  }
                  loading={isLoading()}
                >
                  <>{loadingStates?.activate ?? <LoadingOutlined />} Activate</>
                </Button>
                {showAllActions ?
                  <Button
                    type="primary"
                    spellCheck
                    danger
                    disabled={loadingStates?.revoke}
                    onClick={() =>
                      updateData(5, { status: 0, update_required: 1, updated: 0, is_new: 0 })
                    }
                    loading={isLoading()}
                  >
                    <>{loadingStates?.revoke ?? <LoadingOutlined />} Revoke</>
                  </Button> : null
                }
              </> : null)}
            {showAllActions ? <>
              {data?.status ?
                renderButton({
                  label: <>{loadingStates?.flag ?? <LoadingOutlined />}{data?.flagged ? <>Unflag</> : <>Flag</>}</>,
                  loadingState: loadingStates?.flag,
                  isDisabled: Object.values(loadingStates).some(Boolean),
                  danger: true,
                  ghost: true,
                  onClick: () => {
                    setNdata((d) => ({ ...d, flagged: data?.flag ? 0 : 1 }));
                    confirm({
                      ...(data?.flag ? configUnFlag : configFlag),
                      ...(modalZIndex != null ? { zIndex: modalZIndex + 5 } : {}),
                    });
                  }
                })
                : null}
            </> : null}
            {canUpdateClientEmail && (n_data?.policy_no || data?.policy_no) && (
              <Button
                type="primary"
                ghost
                disabled={isLoading() || updatingEmail}
                onClick={() => {
                  setNewEmail(n_data?.email || data?.email || '');
                  setChangeEmailVisible(true);
                }}
              >
                Change Email
              </Button>
            )}
            {canUpdateClientPhone && (n_data?.policy_no || data?.policy_no) && (
              <Button
                type="primary"
                ghost
                disabled={isLoading() || updatingPhone}
                onClick={() => {
                  setNewPhone(n_data?.phone || data?.phone || '');
                  setChangePhoneVisible(true);
                }}
              >
                Change Phone
              </Button>
            )}
            <Button
              type="dashed"
              disabled={isLoading()}
              danger
              onClick={onclose}
            >
              Close
            </Button>
          </>
        }
        width={800}
        destroyOnClose
      >
        <Row gutter={[24, 8]} style={{ margin: '20px 0' }}>
          <Col span={12}>
            <Divider orientation="left" orientationMargin={0} style={{ margin: 0 }}>Personal Information</Divider>
            <Row gutter={[16, 8]}>
              <Col span={24}>
                {renderField({
                  viewOnly: viewOnly,
                  label: 'Name',
                  isDisabled: !edit || !canEditPersonalDetails,
                  value: n_data?.name,
                  onChange: (e) => setNdata((d) => ({ ...d, name: e.target.value }))
                })}
              </Col>
              <Col span={24}>
                {renderField({
                  viewOnly: viewOnly,
                  label: 'Phone',
                  inputType: 'input',
                  // Login number: changed through "Change Phone" (own permission +
                  // uniqueness check + audit log), like Email, never inline.
                  isDisabled: true,
                  value: n_data?.phone,
                  onChange: (e) => setNdata((d) => ({ ...d, phone: e.target.value }))
                })}
              </Col>
              <Col span={24}>
                {renderField({
                  viewOnly: viewOnly,
                  label: 'Policy No.',
                  inputType: 'input',
                  isDisabled: true,
                  value: n_data?.policy_no,
                  onChange: (e) => setNdata((d) => ({ ...d, policy_no: e.target.value }))
                })}
              </Col>
              <Col span={24}>
                {
                  renderField({
                    viewOnly: viewOnly,
                    label: 'Email',
                    value: n_data?.email,
                    isDisabled: true,
                  })
                }
              </Col>
              <Col span={24}>
                {
                  renderField({
                    viewOnly: viewOnly,
                    label: 'State',
                    value: n_data?.state,
                    isDisabled: !edit || !canEditPersonalDetails,
                    options: states,
                    inputType: 'select',
                    onChange: (e) => setNdata((d) => ({ ...d, state: e }))
                  })
                }
              </Col>
              <Col span={24}>
                {
                  renderField({
                    viewOnly: viewOnly,
                    isDisabled: !edit || !canEditPersonalDetails,
                    label: 'City',
                    value: n_data?.city,
                    onChange: (e) => setNdata((d) => ({ ...d, city: e.target.value }))
                  })
                }
              </Col>

            </Row>
          </Col>
          <Col span={12}>
            <Divider orientation="left" orientationMargin={0} style={{ margin: 0 }}>Premium Details</Divider>
            <Row gutter={[16, 8]}>
              <Col span={24}>
                {renderField({
                  viewOnly: viewOnly,
                  label: 'Insurance Package',
                  inputType: 'select',
                  options: planOptions,
                  isDisabled: !edit || !canEditPremiumDetails,
                  loading: loadingPlans,
                  value: Number.isFinite(Number(n_data?.plans_id)) ? Number(n_data?.plans_id) : undefined,
                  onChange: (e) => {
                    const selected = planOptions.find((plan) => Number(plan?.value) === Number(e));
                    setNdata((d) => ({
                      ...d,
                      plans_id: Number(e),
                      insurance_package: selected?.plan_name || d?.insurance_package,
                    }));
                  }
                })}
              </Col>
              <Col span={24}>
                {renderField({
                  viewOnly: viewOnly,
                  label: 'Premium Type',
                  inputType: 'select',
                  options: policy_type,
                  isDisabled: !edit || !canEditPremiumDetails,
                  value: n_data?.premium_type,
                  onChange: (e) => setNdata((d) => ({ ...d, premium_type: e }))
                })}
              </Col>
              <Col span={24}>
                <Row align={'bottom'}>
                  <Col span={20}>
                    {renderField({
                      viewOnly: viewOnly,
                      label: 'Premium Category',
                      inputType: 'input',
                      isDisabled: true,
                      value: n_data?.category,
                      onChange: (e) => setNdata((d) => ({ ...d, category: e.target.value }))
                    })}
                  </Col>
                  <Col span={4}>
                    {canEditPremiumDetails ? renderButton({
                      danger: true,
                      type: 'dashed',
                      label: 'Change',
                      onClick: () => setUpdateCategory(true)
                    }) : null}
                  </Col>
                </Row>
              </Col>
              {data?.social_no && data?.category?.toLowerCase() === "corporate" ? <Col span={24}>
                {
                  renderField({
                    viewOnly: viewOnly,
                    label: 'Corporate Code',
                    value: socialDetail?.org_name || n_data?.social_no,
                    isDisabled: true,
                    suffix: <>{
                      validatingSocial && !socialDetail ? (
                        <LoadingOutlined style={{ color: "blue" }} />
                      ) : !validatingSocial && socialDetail ? (
                        <CheckOutlined style={{ color: "green" }} />
                      ) : (
                        <WarningOutlined style={{ color: "gold" }} />
                      )
                    }</>
                  })
                }
              </Col> : null}
              <Col span={24}>
                {
                  renderField({
                    viewOnly: viewOnly,
                    label: 'Enrollee Size',
                    value: n_data?.enrolee_size,
                    isDisabled: !edit || !canEditPremiumDetails,
                    onChange: e => setNdata((d) => ({
                      ...d,
                      enrolee_size: e.target.value
                    }))
                  })
                }
              </Col>
              <Col span={24}>
                <Row gutter={[10, 10]}>
                  <Col span={12}>
                    {
                      renderField({
                        viewOnly: viewOnly,
                        isDisabled: true,
                        label: 'Registered On',
                        value: n_data?.date ? (new Date(n_data?.date)).toDateString() : "Invalid Date",
                      })
                    }
                  </Col>
                  <Col span={12}>
                    {
                      renderField({
                        viewOnly: viewOnly,
                        isDisabled: true,
                        label: 'Valid Till',
                        value: n_data?.expiry_date ? (new Date(n_data?.expiry_date)).toDateString() : "Invalid Date",
                      })
                    }
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>
      </Modal>

      <Modal
        rootClassName={modalClassName || undefined}
        className={modalClassName ? `${modalClassName}__dialog` : undefined}
        title={`Change Client Email - ${n_data?.policy_no || data?.policy_no || ''}`}
        open={changeEmailVisible}
        onOk={handleChangeEmail}
        confirmLoading={updatingEmail}
        onCancel={() => setChangeEmailVisible(false)}
        okText="Update Email"
        zIndex={modalZIndex != null ? modalZIndex + 10 : undefined}
      >
        <Row gutter={[12, 12]}>
          <Col span={24}>
            <label style={{ fontWeight: 500 }}>New Email Address</label>
            <Input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="name@example.com"
              maxLength={150}
            />
          </Col>
        </Row>
      </Modal>

      <Modal
        rootClassName={modalClassName || undefined}
        className={modalClassName ? `${modalClassName}__dialog` : undefined}
        title={`Change Client Phone - ${n_data?.policy_no || data?.policy_no || ''}`}
        open={changePhoneVisible}
        onOk={handleChangePhone}
        confirmLoading={updatingPhone}
        onCancel={() => setChangePhoneVisible(false)}
        okText="Update Phone"
        zIndex={modalZIndex != null ? modalZIndex + 10 : undefined}
      >
        <Row gutter={[12, 12]}>
          <Col span={24}>
            <label style={{ fontWeight: 500 }}>New Phone Number</label>
            <Input
              type="tel"
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value)}
              placeholder="08000000000"
              maxLength={20}
            />
            <div style={{ marginTop: 6, fontSize: 12, color: '#888' }}>
              This is the client&apos;s login number. It must not be in use by another policy holder or member.
            </div>
          </Col>
        </Row>
      </Modal>

      {/* Social Code Update Modal */}
      <Modal
        rootClassName={modalClassName || undefined}
        className={modalClassName ? `${modalClassName}__dialog` : undefined}
        title="Plan Category Edit"
        open={updateCategory}
        onOk={updateUseCategory}
        onCancel={() => setUpdateCategory(false)}
        destroyOnClose
        zIndex={modalZIndex != null ? modalZIndex + 20 : undefined}
        footer={canManagePolicyCommercials ? [
          <Button
            type="primary"
            onClick={updateUseCategory}
            // disabled={(!categoryVerified && (categoryUpdateData?.category !== 'self pay' || n_data?.category !== 'self pay')) || ((categoryUpdateData?.category === 'corporate' || n_data?.category === 'corporate') && (categoryUpdateData?.social_no === n_data.social_no || !categoryUpdateData?.social_no?.length))}
            disabled={!(!loading && categoryVerified) || isEqual(normalizedCategoryUpdateData, normalizedFocalData)}
          >
            Update
          </Button>,
          <Button
            type="primary"
            danger
            onClick={() => setUpdateCategory(false)}
          >
            Close
          </Button>,
        ] : null}
      >
        <Row gutter={[12, 12]}>
          <Col span={6}>Policy Category</Col>
          <Col span={18}>
            <Select
              value={categoryUpdateData?.category}
              onChange={(e) => {
                setCategoryUpdateData((d) => ({ ...d, category: e }));
                if (e.toLowerCase() === 'corporate') {
                  setCategoryVerified(false)
                } else {
                  setCategoryVerified(true)
                }
              }}
              options={[
                { label: "Self Pay", value: "self pay" },
                { label: "Corporate", value: "corporate" },
              ]}
              style={{ width: "100%" }}
            />
          </Col>
          {normalizedCategoryValue === "corporate" && (
            <>
              <Col span={6}>Assocaition Code</Col>
              <Col span={14}>
                <Input
                  value={categoryUpdateData?.social_no}
                  onChange={(e) => {
                    setCategoryUpdateData((d) => ({
                      ...d,
                      social_no: e.target.value,
                    }))
                  }}
                  onKeyUp={() => setCategoryVerified(false)}
                  style={{ width: "100%" }}
                />
              </Col>
              <Col span={4}>
                <Button
                  type="dashed"
                  danger
                  onClick={verifyCode}
                  style={{ width: "100%" }}
                disabled={updateCategoryValidating || !hasSocialNo}
                >
                  verify
                </Button>
              </Col>
              <Col span={6}></Col>
              <Col span={18}>{UpdateSocialDetail?.org_name}</Col>
            </>
          )}
        </Row>
      </Modal>

      {/* Dependent Onboarding */}
      <DependantEdit
        style={{ top: 10 }}
        isModalVisible={showEnrolleeAddition}
        handleCancel={() => setShowEnrolleeAddition(false)}
        data={data}
        command={() => setShowEnrolleeAddition(false)}
        key={data?.policy_no}
        modalZIndex={modalZIndex != null ? modalZIndex + 30 : undefined}
      />
    </>
  );
};